// 업무 자료함의 가입 링크를 "PC에 접속기록을 남기지 않고" 여는 통로 (스크린챗 티켓 #162).
//
// 왜 이렇게 하나: 매장에서는 도매처별 가입 URL 을 자료함에 모아두고 개통할 때마다 누른다.
// 그런데 새 탭으로 열면 그 주소가 PC 의 방문 기록에 그대로 남고, 점검이 나왔을 때 그 기록이
// 남아 있으면 영업정지까지 갈 수 있어 매장에 큰 위험이다.
//
// 그래서 링크를 눌러도 **탭을 열지 않는다.** 대신 주소를 클립보드에 복사하고 "시크릿 창에
// 붙여넣으세요" 안내를 띄운다. 시크릿 창을 웹에서 강제로 열어주는 방법은 브라우저 보안 규칙상
// 존재하지 않으므로(모든 브라우저가 막아 둔 규칙), 붙여넣기 한 번은 사용자 몫으로 남는다.
//
// 두 경로가 이 훅 하나를 같이 쓴다 — 자료함 메모 본문 안의 주소, 그리고 '링크' 종류 자료.

import { useCallback, useState } from "react";
import { Modal } from "../ui/modal";

/** 클립보드 복사 — 보안 컨텍스트가 아니면 옛 방식으로 물러난다. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // 권한 거부·비보안 컨텍스트 — 아래 폴백으로 내려간다.
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);

/**
 * 자료함 링크를 기록 없이 여는 안내 창을 붙여 준다.
 *
 * 사용법: `const { openPrivately, guide } = usePrivateLinkGuide();` 로 받아
 * 링크 클릭에서 `openPrivately(url)` 을 부르고, 화면 어딘가에 `{guide}` 를 그려 둔다.
 */
export function usePrivateLinkGuide() {
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const openPrivately = useCallback(async (target: string) => {
    setUrl(target);
    setCopied(await copyText(target));
  }, []);

  const recopy = useCallback(async () => {
    if (url) setCopied(await copyText(url));
  }, [url]);

  const guide = (
    /* 링크를 시크릿 창으로 열라고 그 자리에서 알리는 안내창이다
       (누른 링크가 이 화면 안에서만 정해진다). */
    // eslint-disable-next-line modal-url/prefer-url-modal -- 위 주석 참고(안내창 예외)
    <Modal isOpen={url !== null}
      onClose={() => setUrl(null)}
      className="mx-4 w-full max-w-lg"
    >
      {/* 오른쪽 위 ✕ 자리를 비워 둔다 — 안 비우면 안내 문구가 그 아래로 깔린다. */}
      <div className="p-6 pr-8 sm:pr-10">
        <h2 className="mb-1 text-lg font-semibold text-gray-800 dark:text-white">
          시크릿 창에 붙여넣어 여세요
        </h2>
        <p className="mb-4 text-sm leading-6 text-gray-500 dark:text-gray-400">
          이 주소는 새 탭으로 열지 않습니다 — 일반 창으로 열면 PC 방문 기록에 남기 때문입니다.
          {copied ? " 주소를 복사해 두었으니" : " 아래 주소를 복사해"} 시크릿 창에 붙여넣어 주세요.
          <br />
          시크릿 창을 대신 열어드릴 수는 없습니다 — 브라우저 보안 규칙상 웹 화면이 시크릿 창을
          여는 것은 막혀 있습니다.
        </p>

        <ol className="mb-4 space-y-1 text-sm text-gray-700 dark:text-gray-200">
          <li>
            1. <b>{isMac ? "⌘+Shift+N" : "Ctrl+Shift+N"}</b> 을 눌러 시크릿 창을 엽니다.
            <span className="text-gray-400"> (엣지·웨일은 {isMac ? "⌘" : "Ctrl"}+Shift+P/N)</span>
          </li>
          <li>
            2. 주소창에 <b>{isMac ? "⌘+V" : "Ctrl+V"}</b> 로 붙여넣고 Enter 를 누릅니다.
          </li>
        </ol>

        <div className="mb-4 rounded-lg bg-gray-50 p-3 dark:bg-white/[0.04]">
          <p className="break-all font-mono text-xs leading-5 text-gray-700 dark:text-gray-200">
            {url}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <span
            className={
              copied
                ? "text-xs text-success-600 dark:text-success-500"
                : "text-xs text-gray-500 dark:text-gray-400"
            }
          >
            {copied ? "주소를 복사했습니다." : "복사가 안 되면 위 주소를 직접 선택해 복사해주세요."}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (url) window.open(url, "_blank", "noopener");
                setUrl(null);
              }}
              className="h-9 rounded-lg border border-gray-300 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-white/[0.03]"
            >
              바로 열기
            </button>
            <button
              type="button"
              onClick={() => void recopy()}
              className="h-9 rounded-lg border border-brand-500 px-3 text-sm font-semibold text-brand-500 hover:bg-brand-50 dark:hover:bg-white/[0.03]"
            >
              주소 복사
            </button>
            <button
              type="button"
              onClick={() => setUrl(null)}
              className="h-9 rounded-lg bg-brand-500 px-4 text-sm font-semibold text-white hover:bg-brand-600"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );

  return { openPrivately, guide };
}
